import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addVehicleType } from "../../../actions/admin/vehicletype";
import { vehicleTypeImageOptions } from "../../../utils/dataLists";
import Alert from "../../layout/Alert";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminFormFullRow,
  AdminTextField,
  AdminSelectField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ValueList, summarizeFields } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";
import { VEHICLE_TYPE_LIST, ADD_VEHICLE_TYPE_FIELDS, AMENITY_SLOTS } from "./fields";

// The amenity rows and the outside-Tokyo rates are edit-screen fields, but the record
// still has to be created with them present and empty.
const EMPTY_AMENITIES = AMENITY_SLOTS.reduce(
  (acc, n) => ({ ...acc, [`option${n}Icon`]: "", [`optionText${n}`]: "" }),
  {}
);

const AddVehicleType = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [formData, setFormData] = useState({
    typename: "",
    vehicletypeimage: vehicleTypeImageOptions[0].label,
    hourlyprice: 0,
    description: "",
    maxpassenger: 0,
    maxluggages: 0,
    baseprice: 0,
    subtitle: "",
    ...EMPTY_AMENITIES,
  });

  const { typename, vehicletypeimage, hourlyprice, description, maxluggages, maxpassenger, baseprice } =
    formData;

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const save = useSaveConfirmation({
    save: () => dispatch(addVehicleType(formData, null)),
    onDismiss: () => history.push(VEHICLE_TYPE_LIST),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    save.request(summarizeFields(ADD_VEHICLE_TYPE_FIELDS, formData));
  };

  return (
    <AdminPage title="Add Vehicle Type" actions={<AdminBackLink to={VEHICLE_TYPE_LIST} />}>
      <form onSubmit={onSubmit} className="tw:max-w-[860px]">
        <AdminFormCard title="Vehicle details" columns={2}>
          <AdminTextField
            name="typename"
            label="Type name"
            placeholder="Enter vehicle type"
            value={typename}
            onChange={onChange}
            required
          />

          <AdminSelectField
            name="vehicletypeimage"
            label="Image name"
            options={vehicleTypeImageOptions}
            value={vehicleTypeImageOptions.find((o) => o.label === vehicletypeimage) || null}
            onChange={(option) => setFormData({ ...formData, vehicletypeimage: option.label })}
          />

          <AdminFormFullRow>
            <AdminTextField
              name="description"
              label="Description"
              placeholder="Enter vehicle type description"
              value={description}
              onChange={onChange}
              required
            />
          </AdminFormFullRow>

          <AdminTextField
            name="maxpassenger"
            label="Maximum passengers"
            type="number"
            min="0"
            step="1"
            placeholder="Enter passengers"
            value={maxpassenger}
            onChange={onChange}
            required
          />

          <AdminTextField
            name="maxluggages"
            label="Maximum luggages"
            type="number"
            min="0"
            step="1"
            placeholder="Enter luggages"
            value={maxluggages}
            onChange={onChange}
            required
          />
        </AdminFormCard>

        <AdminFormCard
          title="Pricing"
          description="The rest of the pricing, the amenity rows and the availability cut-off are set on the edit screen once this type exists."
          columns={2}
        >
          <AdminTextField
            name="hourlyprice"
            label="30 minutes rate"
            type="number"
            min="0"
            step="1"
            placeholder="Enter rate"
            value={hourlyprice}
            onChange={onChange}
            required
          />

          <AdminTextField
            name="baseprice"
            label="Base price"
            type="number"
            min="0"
            step="1"
            placeholder="Enter base price"
            value={baseprice}
            onChange={onChange}
            required
          />
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Add Vehicle Type</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Create this vehicle type?"
        description="It will be offered on the customer site as soon as it is saved."
        confirmLabel="Add Vehicle Type"
        busyLabel="Creating..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ValueList rows={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Vehicle type created"
        description="It is live on the customer site now. Closing this returns you to the vehicle type list."
        onClose={save.dismiss}
      >
        <ValueList rows={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default AddVehicleType;
